import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { organizationalUnitService as sharedOrganizationalUnitService } from "../../../shared/api/organizationalUnitService";
import { PermissionName } from "../../../shared/types/permission";
import { organizationalUnitService } from "../shared/api/organizationalUnitService";
import { UserOptionsResource } from "../shared/types";
import {
	OrganizationalUnitFormValues,
	OrganizationalUnitIndexResource,
	OrganizationalUnitSaveRequest,
} from "../shared/types/organizationalUnit";
import { OrganizationalUnitCreateDrawer, OrganizationalUnitUpdateDrawer } from "./OrganizationalUnitDrawer";
import { OrganizationalUnitListContextMenu } from "./OrganizationalUnitListContextMenu";

export const OrganizationalUnitList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		organizationalUnitService,
		{
			entityName: t("Organizational unit"),
			messages: {
				create: t("Organizational unit was saved."),
				update: t("Organizational unit was saved."),
				delete: t("Organizational unit was deleted."),
			},
		},
		[],
		[sharedOrganizationalUnitService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<OrganizationalUnitIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<OrganizationalUnitListContextMenu
						organizationalUnitId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);
	const canCreate = useHasPermission<PermissionName>(["organizationalUnits.create"]);
	const canUpdate = useHasPermission<PermissionName>(["organizationalUnits.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("Organizational units"),
				empty: t("No organizational units available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<OrganizationalUnitCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<OrganizationalUnitUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, mapToRequest(entityFormState))}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(
				organizationalUnitService.listKey,
				organizationalUnitService.index,
			)}
		/>
	);
};

function mapToRequest(values: OrganizationalUnitFormValues): OrganizationalUnitSaveRequest {
	return {
		...values,
		userIds: values.users.map((user: UserOptionsResource) => user.id) ?? [],
	};
}
